import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const PRESET_OPTIONS = [
  { label: '7 Days', days: 7 },
  { label: '15 Days', days: 15 },
  { label: '30 Days', days: 30, default: true },
  { label: '90 Days', days: 90 },
  { label: '180 Days', days: 180 },
  { label: '365 Days', days: 365 },
  { label: 'Custom', days: 'custom' },
];

export default function DateRangeFilter({ 
  selectedDays, 
  onFilterChange,
  onDaysChange,
  startDate,
  customStartDate,
  endDate,
  customEndDate,
  onCustomDateChange,
  onCustomRangeChange,
  compact = false 
}) {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(startDate || customStartDate || '');
  const [customEnd, setCustomEnd] = useState(endDate || customEndDate || '');

  // Support both callback patterns
  const handleDaysChange = (days) => {
    if (onFilterChange) onFilterChange(days);
    if (onDaysChange) onDaysChange(days);
  };

  const handlePresetChange = (option) => {
    if (option.days === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      handleDaysChange(option.days);
    }
  };

  const applyCustomRange = () => {
    if (customStart && customEnd) {
      if (onCustomDateChange) onCustomDateChange(customStart, customEnd);
      if (onCustomRangeChange) onCustomRangeChange(customStart, customEnd);
      setShowCustom(false);
    }
  };

  const getCurrentLabel = () => {
    if (startDate && endDate) {
      return 'Custom Range';
    }
    const option = PRESET_OPTIONS.find(o => o.days === selectedDays);
    return option?.label || '30 Days';
  };

  if (compact) {
    return (
      <div className="relative inline-block">
        <select
          value={selectedDays}
          onChange={(e) => {
            const val = e.target.value;
            if (val === 'custom') {
              setShowCustom(true);
            } else {
              handleDaysChange(Number(val));
            }
          }}
          className="px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg appearance-none cursor-pointer pr-8"
          data-testid="date-filter-select"
        >
          {PRESET_OPTIONS.map((opt) => (
            <option key={opt.days} value={opt.days}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        
        {showCustom && (
          <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 p-4 z-50 w-72">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCustom(false)}
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCustomRange}
                  disabled={!customStart || !customEnd}
                  className="flex-1 px-3 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="date-range-filter">
      <div className="flex items-center gap-1 text-sm text-slate-600">
        <Calendar size={16} />
        <span>Period:</span>
      </div>
      
      <div className="flex gap-1 flex-wrap">
        {PRESET_OPTIONS.filter(o => o.days !== 'custom').map((option) => (
          <button
            key={option.days}
            onClick={() => handlePresetChange(option)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
              selectedDays === option.days && !startDate
                ? 'bg-teal-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`filter-${option.days}`}
          >
            {option.label}
          </button>
        ))}
        
        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
            startDate && endDate
              ? 'bg-teal-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          data-testid="filter-custom"
        >
          Custom
        </button>
      </div>

      {showCustom && (
        <div className="w-full mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            />
          </div>
          <button
            onClick={applyCustomRange}
            disabled={!customStart || !customEnd}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
